import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { tl } from "./ReportsOverviewExtra";

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const METRICS = [
  ["sales", "Satış", tl],
  ["collection", "Tahsilat (temlik dahil)", tl],
  ["door", "Kapı Adedi", (v) => `${v || 0} adet`],
];

const Progress = ({ pct }) => {
  if (pct == null) return <span className="text-xs text-gray-400">hedef yok</span>;
  const color = pct >= 100 ? "bg-green-600" : pct >= 70 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 bg-gray-200 rounded-full h-2"><div className={`${color} h-2 rounded-full`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
      <span className="text-xs font-semibold">%{pct}</span>
    </div>
  );
};

const MonthRow = ({ row, year, api, sessionToken, onSaved }) => {
  const [form, setForm] = useState({ sales_target: row.sales_target, collection_target: row.collection_target, door_target: row.door_target });
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm({ sales_target: row.sales_target, collection_target: row.collection_target, door_target: row.door_target }), [row]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${api}/reports/targets/${year}/${row.month}`, {
        sales_target: Number(form.sales_target) || 0,
        collection_target: Number(form.collection_target) || 0,
        door_target: parseInt(form.door_target, 10) || 0,
      }, { params: { session_token: sessionToken } });
      toast.success(`${MONTHS[row.month - 1]} hedefi kaydedildi`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hedef kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-t align-top" data-testid={`target-row-${row.month}`}>
      <td className="p-2 font-medium">{MONTHS[row.month - 1]}</td>
      {METRICS.map(([key, , fmt]) => (
        <td key={key} className="p-2">
          <Input type="number" min="0" value={form[`${key}_target`]} onChange={(e) => setForm({ ...form, [`${key}_target`]: e.target.value })}
            className="h-8 w-32 text-sm" data-testid={`target-input-${key}-${row.month}`} />
          <div className="text-xs text-gray-600 mt-1">Gerçekleşen: {fmt(row[`${key}_actual`])}</div>
          <Progress pct={row[`${key}_pct`]} />
        </td>
      ))}
      <td className="p-2">
        <Button size="sm" onClick={save} disabled={saving} data-testid={`target-save-${row.month}`}><Save className="h-4 w-4" /></Button>
      </td>
    </tr>
  );
};

export const MonthlyTargets = ({ api, sessionToken, year }) => {
  const [rows, setRows] = useState([]);
  const load = () => axios.get(`${api}/reports/targets`, { params: { session_token: sessionToken, year } })
    .then((r) => setRows(r.data.months)).catch((e) => console.error(e));
  useEffect(() => { load(); }, [year]);

  const currentMonth = new Date().getFullYear() === year ? new Date().getMonth() + 1 : null;
  const cur = rows.find((r) => r.month === currentMonth);
  return (
    <div className="space-y-6 mt-6" data-testid="monthly-targets">
      {cur && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {METRICS.map(([key, label, fmt]) => (
            <Card key={key} data-testid={`target-current-${key}`}>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">{MONTHS[cur.month - 1]} {label} Hedefi</p>
                <p className="text-lg font-bold">{fmt(cur[`${key}_actual`])} <span className="text-sm text-gray-500">/ {fmt(cur[`${key}_target`])}</span></p>
                <Progress pct={cur[`${key}_pct`]} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{year} Aylık Hedefler</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 text-left">
              <tr><th className="p-2">Ay</th><th className="p-2">Satış Hedefi (₺)</th><th className="p-2">Tahsilat Hedefi (₺, temlik dahil)</th><th className="p-2">Kapı Adedi Hedefi</th><th className="p-2" /></tr>
            </thead>
            <tbody>
              {rows.map((row) => <MonthRow key={row.month} row={row} year={year} api={api} sessionToken={sessionToken} onSaved={load} />)}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};
